import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Popconfirm, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

interface EventItem {
    id: number;
    title?: { en: string; vi: string };
    subtitle?: { en: string; vi: string };
    description?: string;
    type?: string;
    active?: boolean;
}

const TYPE_COLOR: Record<string, string> = {
    news: 'blue',
    update: 'cyan',
    announcement: 'gold',
    promotion: 'green',
    challenge: 'purple',
    other: 'default',
};

const EventPage = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState<EventItem[]>([]);
    const [loading, setLoading] = useState(false);

    async function fetchEvents() {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}api/v1/be/events`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (data.status) setEvents(data.data.items);
            else toast.error(data.message || 'Failed to fetch events');
        } catch {
            toast.error('Error fetching events');
        } finally {
            setLoading(false);
        }
    }

    async function toggleEventStatus(eventId: number, currentStatus: boolean) {
        try {
            const response = await fetch(`${API_URL}api/v1/be/events/${eventId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ active: !currentStatus })
            });
            const data = await response.json();
            if (data.status) { toast.success('Event status updated'); fetchEvents(); }
            else toast.error(data.message || 'Failed to update event status');
        } catch {
            toast.error('Error updating event status');
        }
    }

    async function deleteEvent(eventId: number) {
        try {
            const response = await fetch(`${API_URL}api/v1/be/events/${eventId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (data.status) { toast.success('Event deleted'); fetchEvents(); }
            else toast.error(data.message || 'Failed to delete event');
        } catch {
            toast.error('Error deleting event');
        }
    }

    useEffect(() => { fetchEvents(); }, []);

    const columns: ColumnsType<EventItem> = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 70,
            render: (val: number) => <span className="text-xs text-text-muted">#{val}</span>,
        },
        {
            title: 'Title (EN)',
            width: 320,
            render: (_, item) => <span className="text-sm line-clamp-2 block">{item.title?.en || '—'}</span>,
        },
        {
            title: 'Title (VI)',
            width: 320,
            render: (_, item) => <span className="text-sm line-clamp-2 block">{item.title?.vi || '—'}</span>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            render: (val: string) => (
                <Tag color={TYPE_COLOR[val] ?? 'default'} className="capitalize">{val || '—'}</Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'active',
            render: (val: boolean) => (
                <Tag color={val ? 'green' : 'default'}>{val ? 'Active' : 'Inactive'}</Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, item) => (
                <div className="flex flex-col gap-1 w-24" onClick={e => e.stopPropagation()}>
                    <button
                        onClick={() => navigate(`/events/${item.id}`)}
                        className="px-2 py-1 rounded text-sm font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 transition-colors cursor-pointer text-left w-full"
                    >
                        View
                    </button>
                    <button
                        onClick={() => toggleEventStatus(item.id, item.active || false)}
                        className={`px-2 py-1 rounded text-sm font-medium transition-colors cursor-pointer text-left w-full ${item.active ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 hover:bg-yellow-100' : 'bg-green-50 dark:bg-green-900/20 text-green-600 hover:bg-green-100'}`}
                    >
                        {item.active ? 'Deactivate' : 'Activate'}
                    </button>
                    <Popconfirm
                        title={`Delete event #${item.id}?`}
                        description="This action cannot be undone."
                        onConfirm={() => deleteEvent(item.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <button className="px-2 py-1 rounded text-sm font-medium bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 transition-colors cursor-pointer text-left w-full">
                            Delete
                        </button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <SidebarLayout>
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Events</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchEvents}
                            className="p-1.5 rounded hover:bg-bg-hover transition-colors text-text-secondary cursor-pointer"
                            title="Refresh"
                        >
                            <ArrowClockwiseIcon size={16} />
                        </button>
                        <Button type="primary" icon={<PlusIcon size={14} />} onClick={() => navigate('/events/create')}>
                            Create Event
                        </Button>
                    </div>
                </div>

                <div className="bg-bg-card border border-border rounded-sm shadow-card">
                    <Table
                        columns={columns}
                        dataSource={events}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        scroll={{ x: 'max-content' }}
                        pagination={{ pageSize: 10, showSizeChanger: false, showTotal: total => `${total} events` }}
                        onRow={item => ({ onClick: () => navigate(`/events/${item.id}`), className: 'cursor-pointer' })}
                        locale={{ emptyText: <div className="py-10 text-center text-text-muted text-sm">No events found</div> }}
                    />
                </div>
            </div>
        </SidebarLayout>
    );
};

export default EventPage;
